/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module MessageItemsModule
 */

import {
    Component,
    Type,
} from '@angular/core';

import {
    GslbSiteType,
    IGslbHealthMonitorProxy,
    IGslbSite,
    IReplicationPolicy,
    SiteMemberType,
} from 'generated-types';

import { GslbSite } from 'object-types';
import { StringService } from 'string-service';
import { MessageItem } from 'ajs/modules/data-model/factories/message-item.factory';

import {
    withEditChildMessageItemMixin,
} from 'ajs/modules/data-model/mixins/with-edit-child-message-item.mixin';

import {
    HttpMethod,
    HttpWrapper,
} from 'ajs/modules/core/factories/http-wrapper';

import {
    RepeatedMessageItem,
} from 'ajs/modules/data-model/factories/repeated-message-item.factory';

import { DevLoggerService } from 'ng/modules/core/services/dev-logger.service';
import { IpAddrConfigItem } from 'ajs/modules/data-model/factories/ip-addr.config-item.factory';

import {
    GslbSiteModalComponent,
} from 'ng/modules/gslb/components/gslb-site-modal/gslb-site-modal.component';

import {
    GslbSitePreviewComponent,
} from 'ng/modules/gslb/components/gslb-site-modal/gslb-site-preview/gslb-site-preview.component';

import { GSLB } from 'ng/modules/items/factories/gslb.item.factory';
import { IFullModalLayout } from 'ng/modules/core/services/full-modal/full-modal.service';

import {
    IItemParams,
    withFullModalMixin,
} from 'ajs/js/utilities/mixins/with-full-modal.mixin';

import { GslbGeoLocationConfigItem } from 'message-items/gslb-geo-location.config-item.factory';
import { GslbSiteDnsVsConfigItem } from 'message-items/gslb-site-dns-vs.config-item.factory';

const DNS_VSES = 'dns_vses';

type TGslbSitePartial = Omit<IGslbSite, 'ip_addresses' | 'hm_proxies' | 'location' | 'dns_vses' | 'replication_policy'>;

interface IGslbSiteConfig extends TGslbSitePartial {
    ip_addresses: RepeatedMessageItem<IpAddrConfigItem>;
    hm_proxies: RepeatedMessageItem<MessageItem<IGslbHealthMonitorProxy>>;
    dns_vses: RepeatedMessageItem<GslbSiteDnsVsConfigItem>;
    location: GslbGeoLocationConfigItem;
    replication_policy: MessageItem<IReplicationPolicy>;
}

interface IGslbSiteItemParams extends IItemParams {
    gslb: GSLB;
}

const GSLB_SITE_VERIFICATION_URL = '/api/gslbsiteops/verify';

/**
 * @description GslbSite ConfigItem class.
 *
 * @author Hitesh Mandav
 */
export class GslbSiteConfigItem extends withFullModalMixin(
    withEditChildMessageItemMixin<IGslbSiteConfig, typeof MessageItem>(MessageItem),
)<IGslbSiteConfig> {
    public static ngDependencies = [
        'HttpWrapper',
        'devLoggerService',
        StringService,
    ];

    /**
     * Do site verification in case the credentials has been updated.
     */
    public siteVerificationRequired: boolean;

    private readonly httpWrapper: HttpWrapper;

    private readonly devLoggerService: DevLoggerService;

    constructor(args = {}) {
        const extendedArgs = {
            objectName: 'gslbsite',
            objectType: GslbSite,
            windowElement: GslbSiteModalComponent,
            ...args,
        };

        super(extendedArgs);

        const HttpWrapper = this.getNgDependency('HttpWrapper');

        this.httpWrapper = new HttpWrapper();
        this.devLoggerService = this.getNgDependency('devLoggerService');
        this.siteVerificationRequired = false;
    }

    /**
     * Return true if member is `active`.
     */
    public isActiveMember() : boolean {
        const { member_type: memberType } = this.getConfig();

        return memberType === SiteMemberType.GSLB_ACTIVE_MEMBER;
    }

    /**
     * Return array of IP Address.
     */
    public ipAddress(): string[] {
        const { ip_addresses: ipAddresses } = this.getConfig();

        const ipAddress = ipAddresses.config
            .map(ip => ip.toString());

        return ipAddress;
    }

    /**
     * Return GslbSite enabled/disabled state.
     */
    public isEnabled(): boolean {
        return this.getConfig().enabled;
    }

    /**
     * Initialize the location feild.
     */
    public onConfigureUserLocation(): void {
        this.safeSetNewChildByField('location');
    }

    /**
     * Make an API call to verify whether this GslbSite is configured to participate in GSLB
     * configuration.
     */
    public async verifySite(): Promise<void> {
        this.busy = true;
        this.errors = null;

        const payload = {
            username: this.config.username,
            password: this.config.password,
            port: this.config.port,
            ip_addresses: this.config.ip_addresses.flattenConfig(),
        };
        const requestConfig = {
            data: payload,
            method: HttpMethod.POST,
            url: GSLB_SITE_VERIFICATION_URL,
        };

        try {
            const resp = await this.httpWrapper.request(requestConfig);

            this.setConfigAfterVerification(resp.data.rx_uuid);
        } catch (error) {
            this.errors = error.data.error;
            throw error;
        } finally {
            this.busy = false;
        }
    }

    /**
     * Open modal to add a new dns vs.
     */
    public addDnsVs(
        tenantId: string,
        configuredDomains: string[],
        selectedVsId: string[],
    ): void {
        this.addChildMessageItem({
            field: DNS_VSES,
            modalBindings: {
                clusterUuid: this.config.cluster_uuid,
                tenantId,
                configuredDomains,
                selectedVsId,
            },
        });
    }

    /**
     * Open Modal to edit dns vs.
     */
    public editDnsVs(
        dnsVs: GslbSiteDnsVsConfigItem,
        tenantId: string,
        configuredDomains: string[],
        selectedVsId: string[],
    ): void {
        this.editChildMessageItem({
            field: DNS_VSES,
            messageItem: dnsVs,
            modalBindings: {
                clusterUuid: this.config.cluster_uuid,
                tenantId,
                configuredDomains,
                selectedVsId,
            },
        });
    }

    /**
     * Delete selected dns vs.
     */
    public deleteDnsVs(dnsVs: GslbSiteDnsVsConfigItem): void {
        this.config.dns_vses.removeByMessageItem(dnsVs);
    }

    /**
     * Initialize the dns_vses feild.
     */
    public initializeDnsVses(): void {
        this.safeSetNewChildByField('dns_vses');
    }

    /**
     * Delete all dns vs.
     */
    public deleteDnsVses(): void {
        delete this.config.dns_vses;
    }

    /**
     * Remove subdomain from DnsVses of site.
     */
    public removeSubdomainFromSite(subdomain: string): void {
        const { dns_vses: dnsVses } = this.config;

        dnsVses.config.forEach((dnsVs: GslbSiteDnsVsConfigItem) => {
            dnsVs.removeSubdomain(subdomain);
        });
    }

    /**
     * Add subdomain in DnsVses of site.
     */
    public addSubdomainInDnsVses(dnsVsIds: string[], subdomain: string): void {
        const dnsVsIdsToAdd = new Set(dnsVsIds);
        const { dns_vses: dnsVses } = this.config;

        dnsVses.config.forEach((dnsVs: GslbSiteDnsVsConfigItem) => {
            if (dnsVsIds.includes(dnsVs.config.dns_vs_uuid)) {
                dnsVs.addSubdomain(subdomain);
                dnsVsIdsToAdd.delete(dnsVs.config.dns_vs_uuid);
            }
        });

        // There are DNS VS ids that aren't already configured on the GSLB site so they need to be added.
        if (dnsVsIdsToAdd.size > 0) {
            for (const dnsVsId of dnsVsIdsToAdd) {
                dnsVses.add({
                    dns_vs_uuid: dnsVsId,
                    domain_names: [subdomain],
                });
            }
        }
    }

    /**
     * Return all DNS VS uuids which satisfy the below conditions
     * 1. If hostAllSites is true and it does not contain any domain names i.e. domain_names is empty or undefined.
     * 2. If it contains domain names matching with provided name.
     */
    public getDnsVsIdsByDomain(domain: string, hostAllSites = true): string[] {
        return this.getDnsVsesByDomain(domain, hostAllSites).map(vsDns => vsDns.config.dns_vs_uuid);
    }

    /**
     * Return all DNS VS refs which satisfy below conditions
     * 1. If hostAllSites is true and it does not contain any domain names i.e. domain_names is empty or undefined.
     * 2. If it contains domain names matching with provided name.
     */
    public getDnsVsRefsByDomain(domain: string, hostAllSites = true): string[] {
        return this.getDnsVsesByDomain(domain, hostAllSites).map(vsDns => vsDns.config.dnsVsRef);
    }

    /**
     * @override
     * Override the default preview component with custom preview component.
     */
    public async getFullModalProps(
        params: IGslbSiteItemParams,
        modalComponent?: Type<Component>,
    ): Promise<IFullModalLayout> {
        const props = await super.getFullModalProps(params, modalComponent);
        const siteId = params.editable.getConfig().cluster_uuid;
        const isLeader = params.gslb.isLeaderSiteId(siteId);

        return {
            ...props,
            previewComponent: GslbSitePreviewComponent as Type<Component>,
            previewComponentProps: {
                config: params.editable.getConfig(),
                type: GslbSiteType.GSLB_AVI_SITE,
                isLeader,
            },
        };
    }

    /**
     * Return all GslbSiteDnsVsConfigItems which satisfy below conditions
     * 1. If hostAllSites is true and it does not contain any domain names i.e. domain_names is empty or undefined.
     * 2. If it contains domain names matching with provided name.
     */
    private getDnsVsesByDomain(domain: string, hostAllSites = true): GslbSiteDnsVsConfigItem[] {
        const { dns_vses: dnsVses } = this.config;

        return dnsVses.config.filter((dnsVs: GslbSiteDnsVsConfigItem) => {
            if (dnsVs.config.domain_names?.length) {
                return dnsVs.config.domain_names.find(domainName => domainName === domain);
            }

            return hostAllSites;
        });
    }

    /**
     * Set the new cluster UUID
     * If the cluster UUID has changed, remove the old dns_vses.
     */
    private setConfigAfterVerification(clusterId: string): void {
        // if cluster_uuid has changed we remove old DNS VSs
        if (this.config.cluster_uuid !== clusterId) {
            this.config.dns_vses.removeAll();
        }

        this.config.cluster_uuid = clusterId;
    }
}
